// Utility functions for parsing and displaying audit trail changes

interface AuditChange {
  field: string;
  before: any;
  after: any;
  displayName: string;
  format: (value: any) => string;
}

// Define important fields to track and their display formatting
const TRACKED_FIELDS = [
  {
    field: 'total_amount',
    displayName: 'Total Amount',
    format: (value: any) => typeof value === 'number' ? `$${value.toFixed(2)}` : String(value)
  },
  {
    field: 'customer_name',
    displayName: 'Customer Name',
    format: (value: any) => String(value)
  },
  {
    field: 'customer_email', 
    displayName: 'Customer Email',
    format: (value: any) => String(value)
  },
  {
    field: 'status',
    displayName: 'Status',
    format: (value: any) => String(value).toUpperCase()
  },
  {
    field: 'tax_amount',
    displayName: 'Tax Amount', 
    format: (value: any) => typeof value === 'number' ? `$${value.toFixed(2)}` : String(value)
  }
];

// Deep comparison to find specific changes
function findObjectChanges(before: any, after: any, prefix = ''): AuditChange[] {
  const changes: AuditChange[] = [];
  
  if (!before || !after || typeof before !== 'object' || typeof after !== 'object') {
    return changes;
  }

  // Check tracked fields
  for (const config of TRACKED_FIELDS) {
    const beforeValue = before[config.field];
    const afterValue = after[config.field];
    
    if (beforeValue !== undefined && afterValue !== undefined && beforeValue !== afterValue) {
      changes.push({
        field: config.field,
        before: beforeValue,
        after: afterValue,
        displayName: config.displayName,
        format: config.format
      });
    }
  }

  // Check nested objects (like system_data.receipt)
  if (before.system_data?.receipt && after.system_data?.receipt) {
    const receiptChanges = [
      {
        field: 'subtotal',
        displayName: 'Subtotal',
        format: (value: any) => typeof value === 'number' ? `$${value.toFixed(2)}` : String(value)
      },
      {
        field: 'total',
        displayName: 'Receipt Total',
        format: (value: any) => typeof value === 'number' ? `$${value.toFixed(2)}` : String(value)
      },
      {
        field: 'customer_name',
        displayName: 'Receipt Customer',
        format: (value: any) => String(value)
      }
    ];

    for (const config of receiptChanges) {
      const beforeValue = before.system_data.receipt[config.field];
      const afterValue = after.system_data.receipt[config.field];
      
      if (beforeValue !== undefined && afterValue !== undefined && beforeValue !== afterValue) {
        changes.push({
          field: `receipt.${config.field}`,
          before: beforeValue,
          after: afterValue,
          displayName: config.displayName,
          format: config.format
        });
      }
    }
  }

  return changes;
}

// Extract meaningful changes from audit entry
export function extractAuditChanges(entry: any): AuditChange[] {
  if (!entry?.changes?.before || !entry?.changes?.after) {
    return [];
  }

  return findObjectChanges(entry.changes.before, entry.changes.after);
}

// Format changes for display
export function formatAuditChanges(changes: AuditChange[]): string {
  if (changes.length === 0) {
    return 'No specific changes detected';
  }

  return changes
    .map(change => 
      `${change.displayName}: ${change.format(change.before)} → ${change.format(change.after)}`
    )
    .join(', ');
}

// Format audit action description - simple and compact
export function formatAuditAction(entry: any): string {
  const orderId = entry.order_id || 'Unknown Order';
  const action = entry.action === 'update_order' ? 'updated' : 
                 entry.action === 'create_order' ? 'created' : 'modified';
  
  return `Order ${orderId} ${action}`;
}

// Format timestamp for display  
export function formatAuditTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit', 
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return timestamp;
  }
}
